import { FC } from 'react'
import { Stack, Tooltip, Typography } from '@mui/material'
import {
  Activity,
  Camera,
  EscalateToIntrusionWithDescriptionOutdoorActivityType,
  FinalDocumentationOutdoorActivityType,
  OutdoorEngagement,
  TwoWayAudioInteractionOutdoorActivityType
} from '../../../../api/models'
import { MISSING_VALUE } from '../../../../constants'
import { getOutdoorEngagementResolutionCategoryTooltip } from './OutdoorEngagementResolutionCategoryTooltips'
import { findActivityOfType, RRWebSummary } from './customerCareTimelineUtils'
import {
  EMPTY_FAMILIAR_FACE_REVIEW_TEXT,
  EMPTY_FLAGGED_ISSUES_TEXT,
  EMPTY_PERSON_IDENTIFIED_TEXT,
  EMPTY_STATED_PURPOSE_TEXT,
  getAgentNotesValue,
  getCategoryValue,
  getOutcomeValue,
  getFamiliarFaceReviewValue,
  getFlaggedIssuesValue,
  getHandledByValue,
  getPeopleObservedValue,
  getPersonIdentifiedAsValue,
  getReviewedInFeedValue,
  getStatedPurposeValue,
  getTwoWayAudioValue,
  getWhatWasSeenValue,
  UNABLE_TO_VIEW_VIDEO,
  UNKNOWN_DUE_TO_ERROR_TEXT,
  EMPTY_AGENT_NOTES_TEXT
} from './engagementDetailsUtils'
import { CATEGORY_TOOLTIP_SLOT_PROPS } from './tooltipSlotProps'

interface EngagementDetailsProps {
  engagement: OutdoorEngagement | undefined
  activities: Activity[] | undefined
  cameras: Camera[] | undefined
  rrwebSummary: RRWebSummary | undefined
  timezone?: string
}

interface EngagementDetailRowData {
  field: string
  getValue: (engagement: OutdoorEngagement) => string | undefined
  // Shown when hovering the (underlined) value. Ignored when the row has no value.
  getValueTooltip?: (engagement: OutdoorEngagement) => string | undefined
  emptyText: string
  hidden?: boolean
  // The value is derived from activities, so it's dropped (and shown as an error) when activities fail to load.
  needsActivities?: boolean
  // The value is derived from cameras, so it's dropped (and shown as an error) when cameras fail to load.
  needsCameras?: boolean
  // The value comes from the engagement, but whether the row is shown depends on activities. If activities fail
  // to load, the row is shown and an empty value reads as an error, since we can't tell if it should be empty.
  isVisibilityGatedByActivities?: boolean
  // Empty text is grayed out and italic by default; false renders it like a regular value.
  isEmptyTextMuted?: boolean
}

interface EngagementDetailRowProps {
  field: string
  value: string | undefined
  valueTooltip: string | undefined
  emptyText: string
  isEmptyTextMuted: boolean
  isLastRow: boolean
  'data-testid': string
}

const EngagementDetailRow: FC<EngagementDetailRowProps> = ({
  field,
  value,
  valueTooltip,
  emptyText,
  isEmptyTextMuted,
  isLastRow,
  'data-testid': dataTestId
}) => (
  <Stack direction="row" sx={{ width: '100%', borderBottom: isLastRow ? 'none' : '1px solid var(--border-low)' }} data-testid={dataTestId}>
    <Typography
      sx={{
        width: '226px',
        flexShrink: 0,
        padding: 'var(--spacing-s)',
        color: 'var(--text-primary)',
        fontWeight: 500,
        fontSize: 'var(--size-xs)',
        lineHeight: 'var(--line-height-xs)'
      }}
    >
      {field}
    </Typography>
    <Typography
      sx={{
        flex: '1 0 0',
        padding: 'var(--spacing-s)',
        color: value || !isEmptyTextMuted ? 'var(--text-primary)' : 'var(--text-disabled)',
        fontStyle: value || !isEmptyTextMuted ? 'normal' : 'italic',
        fontSize: 'var(--size-xs)',
        lineHeight: 'var(--line-height-xs)'
      }}
    >
      {value && valueTooltip ? (
        <Tooltip title={valueTooltip} placement="right" arrow slotProps={CATEGORY_TOOLTIP_SLOT_PROPS}>
          <span style={{ textDecoration: 'underline' }}>{value}</span>
        </Tooltip>
      ) : (
        (value ?? emptyText)
      )}
    </Typography>
  </Stack>
)

export const EngagementDetails: FC<EngagementDetailsProps> = ({ engagement, activities, cameras, rrwebSummary, timezone }) => {
  const activitiesFailed = !activities
  const camerasFailed = !cameras
  const anyDataFailed = activitiesFailed || !engagement
  const loadedActivities = activities ?? []
  const hasTwoWayAudioActivity = !!findActivityOfType(loadedActivities, TwoWayAudioInteractionOutdoorActivityType.TwoWayAudioInteraction)
  const hasEscalateActivity = !!findActivityOfType(
    loadedActivities,
    EscalateToIntrusionWithDescriptionOutdoorActivityType.EscalateToIntrusionWithDescription
  )
  const hasEscalateOrFinalDocumentationActivity =
    hasEscalateActivity ||
    !!findActivityOfType(loadedActivities, FinalDocumentationOutdoorActivityType.FinalDocumentation)
  const peopleObservedValue = engagement && getPeopleObservedValue(engagement, loadedActivities)

  const rowDefs: EngagementDetailRowData[] = [
    { field: 'Event summary', getValue: e => getWhatWasSeenValue(e, cameras ?? []), emptyText: MISSING_VALUE, needsCameras: true },
    { field: 'Handled by', getValue: e => getHandledByValue(e, loadedActivities), emptyText: MISSING_VALUE, needsActivities: true },
    {
      field: 'Agent action',
      getValue: e => getReviewedInFeedValue(e, loadedActivities, rrwebSummary),
      emptyText: UNABLE_TO_VIEW_VIDEO,
      needsActivities: true
    },
    { field: 'Outcome', getValue: e => getOutcomeValue(e, loadedActivities), emptyText: MISSING_VALUE, needsActivities: true },
    {
      field: 'Category',
      getValue: getCategoryValue,
      getValueTooltip: e => getOutdoorEngagementResolutionCategoryTooltip(e.result?.resolution?.category),
      emptyText: MISSING_VALUE
    },
    {
      field: '2WA communication',
      getValue: e => getTwoWayAudioValue(e, loadedActivities, timezone),
      emptyText: MISSING_VALUE,
      hidden: !anyDataFailed && hasEscalateActivity,
      needsActivities: true
    },
    {
      field: 'Person identified as',
      getValue: getPersonIdentifiedAsValue,
      emptyText: EMPTY_PERSON_IDENTIFIED_TEXT,
      hidden: !anyDataFailed && !hasTwoWayAudioActivity,
      isVisibilityGatedByActivities: true
    },
    {
      field: 'Stated purpose',
      getValue: getStatedPurposeValue,
      emptyText: EMPTY_STATED_PURPOSE_TEXT,
      hidden: !anyDataFailed && !hasTwoWayAudioActivity,
      isVisibilityGatedByActivities: true
    },
    {
      field: 'Flagged issues',
      getValue: getFlaggedIssuesValue,
      emptyText: EMPTY_FLAGGED_ISSUES_TEXT,
      hidden: !anyDataFailed && !hasTwoWayAudioActivity,
      isVisibilityGatedByActivities: true
    },
    {
      field: 'Monitoring agent notes',
      getValue: e => getAgentNotesValue(e, loadedActivities),
      emptyText: EMPTY_AGENT_NOTES_TEXT,
      needsActivities: true
    },
    {
      field: 'People observed',
      getValue: () => peopleObservedValue,
      emptyText: MISSING_VALUE,
      hidden: !anyDataFailed && (!hasEscalateOrFinalDocumentationActivity || !peopleObservedValue),
      needsActivities: true
    },
    {
      field: 'Familiar face review',
      getValue: () => getFamiliarFaceReviewValue(loadedActivities),
      emptyText: EMPTY_FAMILIAR_FACE_REVIEW_TEXT,
      isEmptyTextMuted: false,
      needsActivities: true
    }
  ]
  const rows = rowDefs.map(row => {
    // Values that depend on activities or cameras can't be trusted without them, so they're dropped when those failed to load.
    const isMissingDependency = (activitiesFailed && row.needsActivities) || (camerasFailed && row.needsCameras)
    const value = engagement && !isMissingDependency ? row.getValue(engagement) : undefined
    const showError = !value && (!engagement || isMissingDependency || (activitiesFailed && row.isVisibilityGatedByActivities))
    return {
      ...row,
      value,
      valueTooltip: value && engagement ? row.getValueTooltip?.(engagement) : undefined,
      emptyText: showError ? UNKNOWN_DUE_TO_ERROR_TEXT : row.emptyText,
      isEmptyTextMuted: showError || (row.isEmptyTextMuted ?? true)
    }
  })

  return (
    <Stack
      sx={{
        gap: 'var(--spacing-m)',
        padding: 'var(--spacing-m)',
        width: '100%',
        backgroundColor: 'var(--bg-surface)',
        border: '1px solid var(--border-medium)',
        borderRadius: 'var(--border-radius-xl)',
        boxShadow: '0px 1px 2px 0px rgba(28,25,23,0.05)'
      }}
      data-testid="engagement-details"
    >
      <Typography sx={{ color: 'var(--text-primary)', fontSize: 'var(--size-s)', fontWeight: 500, lineHeight: 'var(--line-height-s)' }}>
        Event Details
      </Typography>
      <Stack sx={{ width: '100%' }}>
        {rows.map(
          (row, index) =>
            !row.hidden && (
              <EngagementDetailRow
                key={row.field}
                field={row.field}
                value={row.value}
                valueTooltip={row.valueTooltip}
                emptyText={row.emptyText}
                isEmptyTextMuted={row.isEmptyTextMuted}
                isLastRow={index === rows.length - 1}
                data-testid={`engagement-details-row-${index}`}
              />
            )
        )}
      </Stack>
    </Stack>
  )
}
